// src/pages/BeritaDuka.jsx

import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import PageTransitionWrapper from '../components/layout/PageTransitionWrapper';
import { MdInfoOutline, MdCalendarToday, MdHome, MdAccessTime, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { extractObituaryDetails } from '../utils/BeritaParser';

const EmptyState = () => (
    <div className="text-center py-20 px-6">
        <MdInfoOutline className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-2xl text-gray-600 font-medium mb-2">
            Saat ini tidak ada berita duka.
        </p>
        <p className="text-md text-gray-500">
            Informasi berita duka akan ditampilkan di sini jika tersedia.
        </p>
    </div>
);

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    return (
        <div className="flex justify-center items-center space-x-2 my-8">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
            >
                <MdChevronLeft className="w-6 h-6" />
            </button>
            {pages.map(page => (
                <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${page === currentPage ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                    {page}
                </button>
            ))}
            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
            >
                <MdChevronRight className="w-6 h-6" />
            </button>
        </div>
    );
};

const BeritaDuka = () => {
    const [obituaries, setObituaries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState(null);
    const [expandedImageId, setExpandedImageId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    useEffect(() => {
        let isCancelled = false;
        let progressInterval;
        const MIN_LOADING_TIME = 1000;
        const DELAY_AFTER_LOAD = 700;

        const fetchBeritaDuka = async () => {
            setIsLoading(true);
            setProgress(0);
            setError(null);

            const startTime = Date.now();
            progressInterval = setInterval(() => {
                const elapsedTime = Date.now() - startTime;
                let calculatedProgress = (elapsedTime / MIN_LOADING_TIME) * 100;
                if (calculatedProgress > 99) calculatedProgress = 99;
                if (!isCancelled) {
                    setProgress(Math.floor(calculatedProgress));
                }
            }, 50);

            try {
                const [response] = await Promise.all([
                    apiClient.getBeritaDukaList({ page: currentPage }),
                    new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME))
                ]);

                if (!isCancelled) {
                    setObituaries(response || []);
                    setTotalPages(response.last_page || 0);
                    setCurrentPage(response.current_page || 1);
                }
            } catch (err) {
                if (!isCancelled) {
                    const errorMessage = err.message || 'Gagal mengambil berita duka. Silakan coba lagi nanti.';
                    setError(errorMessage);
                    console.error('API Fetch Error:', err);
                }
            } finally {
                clearInterval(progressInterval);
                if (!isCancelled) {
                    setProgress(100);
                    setTimeout(() => {
                        setIsLoading(false);
                    }, DELAY_AFTER_LOAD);
                }
            }
        };

        fetchBeritaDuka();

        return () => {
            isCancelled = true;
            clearInterval(progressInterval);
        };
    }, [currentPage]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const toggleImageExpansion = (id) => {
        setExpandedImageId(expandedImageId === id ? null : id);
    };

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <ErrorDisplay message={error} />
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* 🌟 Header yang Dipercantik */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 relative overflow-hidden border-b-8 border-blue-600">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-white transform skew-x-3 -translate-x-1/2 opacity-70"></div>

                    <div className="relative z-10">
                        <h1 className="text-4xl sm:text-5xl font-black text-gray-900 text-center mb-2">
                            BERITA DUKA
                        </h1>
                        <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto font-medium">
                            Turut berduka cita yang sedalam-dalamnya atas berpulangnya saudara/i kita ke Rumah Bapa di Surga. Kiranya damai sejahtera menyertai keluarga yang ditinggalkan.
                        </p>
                    </div>
                </div>

                {/* Bagian Daftar Berita Duka */}
                <div className="mt-10 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 min-h-[500px] relative">
                    {isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingSpinner progress={progress} />
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            {obituaries.length === 0 ? (
                                <EmptyState />
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                                        {obituaries?.map((obituary) => {
                                            const details = extractObituaryDetails(obituary);
                                            return (
                                                <div
                                                    key={obituary.id}
                                                    className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transform transition-all duration-500 hover:shadow-2xl hover:border-gray-300 flex flex-col group"
                                                >
                                                    <div className="p-6 flex flex-col items-center">
                                                        {/* Image Section - Dibuat lebih fleksibel dan berfokus */}
                                                        <div
                                                            className={`flex-shrink-0 mb-6 cursor-pointer transform transition-all duration-300 ${expandedImageId === obituary.id ? 'w-full h-auto' : 'w-48 h-64'}`}
                                                            onClick={() => toggleImageExpansion(obituary.id)}
                                                        >
                                                            <img
                                                                src={obituary.image}
                                                                alt={`Foto Duka - ${details.nama || 'Almarhum/ah'}`}
                                                                className={`w-full h-full object-cover rounded-lg shadow-md border-4 border-gray-400 transition-all duration-300 group-hover:border-blue-400 ${expandedImageId === obituary.id ? 'scale-105 shadow-xl' : ''}`}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = 'https://via.placeholder.com/192x256.png?text=Foto+Tidak+Tersedia';
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Details Section */}
                                                        <div className="text-center w-full mt-4">
                                                            <p className="text-xs font-semibold text-blue-600 uppercase tracking-widest mb-2">
                                                                {details.judul || 'Berita Duka'}
                                                            </p>
                                                            <h3 className="text-2xl font-extrabold text-gray-900 border-b pb-2 mb-4">
                                                                {details.nama || 'Nama Almarhum/ah'}
                                                            </h3>
                                                            <p className="text-sm font-medium text-gray-500 mb-4">
                                                                GPIB Kasih Karunia Badung Bali
                                                            </p>

                                                            <div className="text-left space-y-3">
                                                                {details.wafat && (
                                                                    <p className="flex items-center text-base text-gray-700 font-semibold">
                                                                        <MdCalendarToday className="mr-3 text-lg text-red-500 flex-shrink-0" />
                                                                        **Wafat:** {details.wafat}
                                                                    </p>
                                                                )}
                                                                {details.usia && (
                                                                    <p className="flex items-center text-base text-gray-700">
                                                                        <MdAccessTime className="mr-3 text-lg text-gray-500 flex-shrink-0" />
                                                                        **Usia:** {details.usia}
                                                                    </p>
                                                                )}
                                                                {details.disemayamkan && (
                                                                    <p className="flex items-start text-base text-gray-700">
                                                                        <MdHome className="mr-3 mt-1 text-lg text-green-600 flex-shrink-0" />
                                                                        <span className="leading-tight">**Disemayamkan:** {details.disemayamkan}</span>
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {details.keterangan.length > 0 && (
                                                                <div className="mt-5 pt-3 border-t border-gray-100 text-left">
                                                                    <h4 className="flex items-center text-sm font-semibold text-gray-600 mb-2">
                                                                        <MdInfoOutline className="mr-1 text-base text-blue-500" /> Keterangan
                                                                    </h4>
                                                                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-4">
                                                                        {details.keterangan.map((item, index) => (
                                                                            <li key={index}>{item}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}

                                                            <p className="text-xs text-gray-400 mt-4 pt-3 border-t border-dashed">
                                                                Dipublikasikan: {obituary.created_at
                                                                    ? new Date(obituary.created_at).toLocaleDateString('id-ID', {
                                                                        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                                                                    })
                                                                    : 'Tanggal tidak tersedia'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {totalPages > 1 && (
                                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                                    )}
                                </>
                            )}
                        </PageTransitionWrapper>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BeritaDuka;